import React from "react";

import { useHistory } from "react-router-dom";

import ROUTES from "renter/config/routes";

import { useWithdrawApplication } from "shared/hooks/api";
import { useVeroFlag } from "shared/utils/featureFlags";
import { trackEvent } from "shared/utils/trackEvent";
import { VeroFlag } from "shared/utils/veroFlags";
import useModal from "shared/hooks/useModal";

import EditApplicationModal from "./EditApplication";
import EditApplicationLegacyModal from "./EditApplicationLegacy";

interface EditApplicationContainerProps {
  applicationId: number;
  apartment: string;
  useV1ExpressionEngine?: boolean;
}

const EditApplicationContainer = ({
  applicationId,
  apartment,
  useV1ExpressionEngine,
}: EditApplicationContainerProps) => {
  const { closeActiveModalDialog } = useModal();
  const history = useHistory();
  const { withdrawApplication, isWithdrawApplication } =
    useWithdrawApplication(applicationId);
  const isV1AppReEntryEnabled = useVeroFlag(VeroFlag.useV1AppReEntry);

  const showOverlay = () => {
    window.addEventListener("pageshow", (event) => {
      if (event.persisted) {
        window.location.reload();
        return;
      }
      removeOverlay();
    });
    document.getElementById("root").style.display = "none";
    const overlay = document.createElement("div");
    overlay.id = "redirect-overlay";
    overlay.style.cssText =
      "position:fixed;inset:0;z-index:9999;background:rgba(255,255,255,0.82);backdrop-filter:blur(4px);display:flex;flex-direction:column;justify-content:center;align-items:center;gap:16px";
    overlay.innerHTML = `
      <style>@keyframes vero-spin{to{transform:rotate(360deg)}}</style>
      <div style="width:40px;height:40px;border:3px solid #EDE7F6;border-top-color:#A374F5;border-radius:50%;animation:vero-spin 0.6s linear infinite"></div>
      <span style="font-family:Work Sans,sans-serif;font-size:16px;font-weight:500;color:#2D2943">Getting your application ready...</span>`;
    document.body.appendChild(overlay);
  };

  const removeOverlay = () => {
    document.getElementById("redirect-overlay")?.remove();
    document.getElementById("root").style.display = "";
  };

  const submit = () => {
    if (isV1AppReEntryEnabled && useV1ExpressionEngine) {
      showOverlay();
    }
    withdrawApplication(null, {
      onSuccess: async () => {
        trackEvent("Application Withdrawn for Edit", { applicationId });
        await closeActiveModalDialog();
        if (isV1AppReEntryEnabled && useV1ExpressionEngine) {
          window.location.href = `${window.location.origin}/applicant/workflow/${applicationId}/my-application`;
        } else {
          removeOverlay();
          history.push(ROUTES.applications);
        }
      },
      onError: () => {
        removeOverlay();
      },
    });
  };

  if (!isV1AppReEntryEnabled) {
    return (
      <EditApplicationLegacyModal
        submit={submit}
        loading={isWithdrawApplication}
        apartment={apartment}
      />
    );
  }

  return (
    <EditApplicationModal
      submit={submit}
      loading={isWithdrawApplication}
      apartment={apartment}
      onClose={closeActiveModalDialog}
    />
  );
};

export default EditApplicationContainer;
